/**
 * The centre of the dashboard: the assistant's face, greeting built from the
 * summary counts, recommended actions, and the composer that hands off to the
 * agent workspace. Sending never shows a transcript here.
 */
import type { FC, ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import type { PanelAgent } from "@/features/agents/agentPanelStore";
import { AssistantAvatar } from "@/features/agents/components/AssistantAvatar";
import type { AgentMessageMetadata } from "@/features/agents/useAgentChat";
import { cn } from "@/lib/utils";

import { greetingSummary, salutation } from "../greeting";
import type {
  ClientGreetingCounts,
  DashboardMode,
  PracticeGreetingCounts,
  Recommendation
} from "../types";
import { DraftComposer } from "./DraftComposer";
import { RecommendedActions } from "./RecommendedActions";

type Props = {
  mode: DashboardMode;
  agent: PanelAgent;
  agentLabel: string;
  /** The assistant's avatar seed (its scope key); the focus when omitted. */
  seed?: string;
  /** The signed-in user's first name. */
  firstName: string | null | undefined;
  /** Client mode: the client's first name ("Maria has 2 …"). */
  subject?: string | null;
  counts: PracticeGreetingCounts | ClientGreetingCounts | null | undefined;
  countsLoading?: boolean;
  recommendations: Recommendation[];
  recommendationsLoading?: boolean;
  onRegenerate?: () => void;
  /** Lets the user dismiss an alert chip; omitted, alerts stay put. */
  onDismiss?: (rec: Recommendation) => void;
  /** `metadata` carries the research sources picked in the composer. */
  onSend: (text: string, metadata?: AgentMessageMetadata) => void;
  isSending?: boolean;
  /** Rendered between the greeting and the chips (e.g. a scope chip). */
  children?: ReactNode;
  /** Client mode: who the client is, in place of the assistant's greeting. */
  identity?: ReactNode;
  className?: string;
};

export const DashboardHero: FC<Props> = ({
  mode,
  agent,
  agentLabel,
  seed,
  firstName,
  subject,
  counts,
  countsLoading = false,
  recommendations,
  recommendationsLoading = false,
  onRegenerate,
  onDismiss,
  onSend,
  isSending = false,
  children,
  identity,
  className
}) => {
  const summary = greetingSummary(mode, counts, subject);
  const idle =
    mode === "practice"
      ? "Nothing is waiting on you right now."
      : `Nothing is waiting on ${
          (subject ?? "").trim() || "this client"
        } right now.`;

  return (
    <div
      className={cn("flex w-full flex-col items-center gap-4", className)}
      data-testid="dashboard-hero"
    >
      {identity ? (
        <div className="w-full">{identity}</div>
      ) : (
        <div className="flex flex-col items-center gap-3 text-center">
          <AssistantAvatar agent={agent} seed={seed ?? agent} size="lg" />
          <h1
            className="text-xl leading-7 font-medium tracking-tight"
            data-testid="hero-greeting"
          >
            {salutation(firstName)}
          </h1>
          {countsLoading && !counts ? (
            <Skeleton
              className="h-4 w-64 rounded-sm"
              data-testid="hero-summary-loading"
            />
          ) : (
            <p
              className="max-w-md text-base leading-5 text-muted-foreground"
              data-testid="hero-summary"
            >
              {summary ?? idle}
            </p>
          )}
        </div>
      )}

      {children}

      <RecommendedActions
        items={recommendations}
        seed={seed ?? agent}
        isLoading={recommendationsLoading}
        onPrompt={onSend}
        onRegenerate={onRegenerate}
        onDismiss={onDismiss}
      />

      <DraftComposer
        draftKey={`dashboard:${seed ?? agent}`}
        agentLabel={agentLabel}
        onSend={onSend}
        isSending={isSending}
        placeholder={
          mode === "practice"
            ? `Ask ${agentLabel} anything about the practice…`
            : `Ask ${agentLabel} about ${
                (subject ?? "").trim() || "this client"
              }…`
        }
      />
    </div>
  );
};
